import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

export interface State {
  optionLanguageData: object | null;
  isLoading: boolean;
  error: unknown;
}

export type LanguageSettingsStoreState = State;

class LanguageSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('language-settings');
  }

  override createInitialState() {
    return {
      optionLanguageData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const optionLanguageData = await api.i18n.getOptionLanguage();

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        optionLanguageData: optionLanguageData as unknown as object,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, this.key);

    return { languageSettings: this.store(selectAll, shallow) };
  }

  async setOptionLanguage({ value }: { value: string }) {
    const api = this.getApi();
    const result = (await api.i18n.setOptionLanguage({ value })) as unknown as Promise<{
      value: string;
    }>;
    this.set({ optionLanguageData: result });
  }
}

function selectAll(state: LanguageSettingsStoreState) {
  return {
    ...selectBase(state),
    optionLanguageData: state.optionLanguageData,
  };
}

export const languageSettingsStore = new LanguageSettingsStore();
